import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { caseDetails, casesList } from '@/mocks/cases';

export default function CaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const detail = slug ? caseDetails[slug] : null;

  if (!detail) {
    return <Navigate to="/cases" replace />;
  }

  const otherCases = casesList.filter((c) => c.slug !== slug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[420px] md:h-[500px] overflow-hidden">
          <img
            src={detail.heroImage}
            alt={detail.company}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          <div className="relative h-full flex flex-col justify-center px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-6 flex items-center gap-2 text-sm text-white/70">
              <Link to="/" className="hover:text-white transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/cases" className="hover:text-white transition-colors">
                应用案例
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white font-medium">{detail.company}</span>
            </nav>
            <div className="flex items-center gap-4">
              {detail.logoText && (
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                  <span className="text-xl md:text-2xl font-black text-white font-heading">
                    {detail.logoText}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-3xl md:text-5xl font-black text-white font-heading tracking-wide">
                  {detail.company}
                </h1>
                <p className="mt-2 text-base md:text-lg text-white/70 font-label">
                  {detail.shortName} · {detail.project}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Overview + Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              {/* Left: Company Desc */}
              <div>
                <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
                  客户背景
                </span>
                <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                  {detail.company}
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
                <p className="mt-6 text-sm md:text-base text-foreground-300 leading-loose">
                  {detail.companyDesc}
                </p>
              </div>

              {/* Right: Stats */}
              <div>
                <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
                  业务规模
                </span>
                <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                  业务规模情况
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {detail.stats.map((stat) => (
                    <div
                      key={stat.label}
                      className="p-5 rounded-lg bg-background-100 border border-background-200/50"
                    >
                      <p className="text-xs text-foreground-400 font-label mb-1">
                        {stat.label}
                      </p>
                      <p className="text-2xl md:text-3xl font-black text-primary-400 font-heading">
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Business Demands */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              需求分析
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              业务需求
            </h2>
            <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            <div className="mt-8 flex flex-wrap gap-3">
              {detail.demands.map((demand) => (
                <span
                  key={demand}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-background-50 border border-background-200/50 text-sm text-foreground-200"
                >
                  <i className="ri-checkbox-circle-line text-primary-400" />
                  {demand}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              问题诊断
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              客户痛点
            </h2>
            <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            <div className="mt-8 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/50 border-l-4 border-l-accent-500">
              <p className="text-sm md:text-base text-foreground-300 leading-loose">
                {detail.painPoints}
              </p>
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              实施路径
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              {detail.solutionTitle}
            </h2>
            <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <p className="text-sm md:text-base text-foreground-300 leading-loose">
                {detail.solutionDesc}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {detail.solutionFeatures.map((feature, index) => (
                  <div
                    key={feature}
                    className="p-5 rounded-lg bg-background-50 border border-background-200/50"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-3">
                      <i className="ri-check-double-line text-lg" />
                    </div>
                    <p className="text-sm font-medium text-foreground-200 leading-relaxed">
                      {feature}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Scheme (Integrated HW + SW) */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              方案架构
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              {detail.schemeTitle}
            </h2>
            <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />

            <div className="mt-8 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/50">
              <p className="text-sm md:text-base text-foreground-300 leading-loose">
                {detail.schemeDesc}
              </p>
              {detail.schemeNote && (
                <p className="mt-4 text-sm text-foreground-400 border-t border-background-200/50 pt-4">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-information-line text-accent-500" />
                    {detail.schemeNote}
                  </span>
                </p>
              )}
            </div>

            {/* Architecture flow diagram */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/50 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-4">
                  <i className="ri-server-line text-2xl" />
                </div>
                <h4 className="text-base font-bold text-foreground-50 mb-2">
                  Cloud USB Server
                </h4>
                <p className="text-sm text-foreground-300">
                  U盾集中柜硬件支撑，U盾统一接入集中管理
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/50 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mb-4">
                  <i className="ri-robot-2-line text-2xl" />
                </div>
                <h4 className="text-base font-bold text-foreground-50 mb-2">
                  AI 网银智能体
                </h4>
                <p className="text-sm text-foreground-300">
                  智能调度、自动登录、自动下载、自动制单
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/50 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-500 mb-4">
                  <i className="ri-database-2-line text-2xl" />
                </div>
                <h4 className="text-base font-bold text-foreground-50 mb-2">
                  ERP / 资金系统
                </h4>
                <p className="text-sm text-foreground-300">
                  数据归档、校验、同步至ERP及业务管理系统
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Project Value (if any) */}
        {detail.valueTitle && detail.valueItems && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
              <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
                成果价值
              </span>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                {detail.valueTitle}
              </h2>
              <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {detail.valueItems.map((item, index) => (
                  <div
                    key={item}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/50"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mb-3">
                      <i className="ri-bar-chart-box-line text-lg" />
                    </div>
                    <p className="text-sm font-medium text-foreground-200 leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Other Cases */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              更多案例
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              其他客户案例
            </h2>
            <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {otherCases.map((item) => (
                <Link
                  key={item.slug}
                  to={`/cases/${item.slug}`}
                  className="group relative flex items-center gap-5 p-5 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/40 transition-all duration-300"
                >
                  <img
                    src={item.cardImage}
                    alt={item.company}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-lg object-cover shrink-0 group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium mb-2">
                      {item.industry}
                    </span>
                    <h4 className="text-lg font-bold text-foreground-50 group-hover:text-primary-500 transition-colors">
                      {item.company}
                    </h4>
                    <p className="mt-1 text-sm text-foreground-300 line-clamp-2">
                      {item.summary}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-sm text-primary-500">
                      <span>查看详情</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}